import {
  DialogContent,
  Dialog,
  DialogProps,
  useEventCallback,
  DialogActions,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { styled } from '@mui/styles';
import { PmiButton } from 'shell/components/shared/button';
import { memo, ReactElement, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { BaseTableRows } from 'shell/components/shared/table/types';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import FormChangeParenthood from './form-family';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { OtherFamiliar } from 'shell/models/other-familiars';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { InterestedPeopleData } from 'shell/models/practice';
import SearchMessages, { MessageType, Message } from 'shell/components/shared/search-messages';
import { TypeRegister } from 'practice/constants/type-register';
import { TypeInitiative } from 'practice/constants/practice-management-step';
import PmiTextField from 'shell/components/shared/text-field';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {}
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

const errorMinorenneMessagge: Array<Message> = [
  {
    id: 0,
    value: 'Attenzione nella nuova famiglia è presenza un minore',
  },
];
export interface ModalChangeParenthoodVM extends BaseTableRows {
  fiscalCode?: string;
  name?: string;
  surname?: string;
  parenthoodValue?: string;
  parenthoodElement?: ReactElement;
  element?: OtherFamiliar;
  elementFamilyPractice?: InterestedPeopleData;
  isMinorenne?: boolean;
  isIntestatarioPratica?: boolean;
}

interface ModalChangeParenthoodProps {
  open: boolean;
  onClose: (isConfirm: boolean) => void;
  codeEnte?: string;
  typeRegister?: string;
  yearId?: string;
  progressive?: string;
  otherFamiliars?: Array<ModalChangeParenthoodVM>;
  newFamiliars?: Array<ModalChangeParenthoodVM>;
  fromPerson?: string;
}

export type FormModal = {
  otherFamiliars: Array<ModalChangeParenthoodVM>;
  newFamiliars: Array<ModalChangeParenthoodVM>;
  reason?: string;
};

const ModalChangeParenthoodComponent = ({
  open,
  onClose,
  codeEnte,
  typeRegister,
  yearId,
  progressive,
  otherFamiliars, // familiari che non si spostano
  newFamiliars,
  fromPerson,
}: ModalChangeParenthoodProps): React.ReactElement => {
  const [isMinorenne, setIsMinorenne] = useState({ open: false, withMinorenni: false });

  // useEffect(() => {
  //   const withMinorenni = (otherFamiliars?.filter((item) => item.isMinorenne) || []).length > 0;

  //   if (withMinorenni) {
  //     setIsMinorenne({ open: true, withMinorenni: true });
  //   }
  // }, [otherFamiliars]);

  const onSave = useHttpClient(`${__PRACTICE_API_URL__}/persona-parentela/inserisci`, {
    options: {
      method: 'POST',
    },
  });

  const onSubmit = useEventCallback((values: FormModal) => {
    (
      onSave({
        data: {
          codiceEnte: codeEnte,
          tipoRegistro: typeRegister,
          idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
          progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
          note: values.reason,
          altriFamiliari: values.otherFamiliars?.map((member) => ({
            ...member.element,
            codAnprParentelaNew: member.parenthoodValue
              ? Number.parseInt(member.parenthoodValue)
              : undefined,
          })),
          nuoviFamiliari: values.newFamiliars
            .filter((member) => !member.isIntestatarioPratica)
            .map((member) => ({
              ...member.elementFamilyPractice,
              codAnprParentela: member.parenthoodValue
                ? Number.parseInt(member.parenthoodValue)
                : undefined,
            })),
        },
      }) as ResponsePromise<void>
    ).then(() => {
      onClose(true);
    });
  });

  const formContext = useFormik<FormModal>({
    initialValues: {
      otherFamiliars: otherFamiliars || [],
      newFamiliars: newFamiliars || [],
      reason: '',
    },
    onSubmit,
    enableReinitialize: true,
    validationSchema: yup
      .object()
      .shape({
        otherFamiliars: yup.array(),
        newFamiliars: yup.array(),
        reason: yup.string(),
      })
      .test('', '', (values) => {
        const castedValues = values as unknown as FormModal;

        const existHeadOtherFamiliars = castedValues.otherFamiliars?.some(
          (item) => item.parenthoodValue === '1'
        );

        if (
          castedValues.otherFamiliars &&
          castedValues.otherFamiliars.length > 0 &&
          castedValues.otherFamiliars?.every((el) => el.isMinorenne) &&
          !castedValues.reason
        ) {
          return new yup.ValidationError('Campo obbligatorio', undefined, 'reason');
        }

        if (
          !existHeadOtherFamiliars &&
          castedValues.otherFamiliars?.length === 1 &&
          typeRegister !== TypeRegister.Unavailable &&
          typeRegister !== TypeRegister.UntraceableCensus &&
          fromPerson !== TypeInitiative.idUfficio &&
          castedValues.otherFamiliars?.some((familiar) => familiar.isMinorenne) &&
          !castedValues.newFamiliars?.some((item) => item.elementFamilyPractice?.flagAire === '1')
        ) {
          return new yup.ValidationError(
            "L'unico membro della famiglia è minorenne",
            undefined,
            'otherFamiliars'
          );
        }

        if (
          !existHeadOtherFamiliars &&
          castedValues.otherFamiliars?.length > 0 &&
          typeRegister !== TypeRegister.Unavailable &&
          typeRegister !== TypeRegister.UntraceableCensus
        ) {
          return new yup.ValidationError(
            'Manca il capo della vecchia famiglia',
            undefined,
            'otherFamiliars'
          );
        }

        const existHeadNewFamily = castedValues.newFamiliars?.some(
          (item) => item.parenthoodValue === '1'
        );

        if (
          !existHeadNewFamily &&
          castedValues.newFamiliars?.length > 0 &&
          fromPerson !== 'N' &&
          typeRegister !== TypeRegister.UntraceableCensus &&
          typeRegister !== TypeRegister.Unavailable
        ) {
          return new yup.ValidationError(
            'Manca il capo della nuova famiglia',
            undefined,
            'newFamiliars'
          );
        }

        return true;
      }),
  });

  const onChangeOtherFamiliars = useEventCallback((values: Array<ModalChangeParenthoodVM>) => {
    formContext.setFieldValue('otherFamiliars', values);
  });

  const onChangeNewFamiliars = useEventCallback((values: Array<ModalChangeParenthoodVM>) => {
    formContext.setFieldValue('newFamiliars', values);
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

  const disabledOnConfirm = useMemo(() => {
    if (!(formContext.values.otherFamiliars?.length > 0)) {
      return false;
    }
    const withIntestatario =
      formContext.values.otherFamiliars?.filter((item) => item.parenthoodValue?.toString() === '1')
        ?.length > 0;

    if (!withIntestatario) {
      return true;
    }

    return false;
  }, [formContext.values.otherFamiliars]);

  return (
    <PmiDialog
      open={open}
      fullScreen={fullScreen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Cambio parentela famiglia
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2}>
          {formContext.values.otherFamiliars?.length > 0 ? (
            <>
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Warning}
                  title="Warning"
                  messages={errorMinorenneMessagge}
                  visibility={isMinorenne.open}
                  onChangeVisibility={() =>
                    setIsMinorenne({ open: false, withMinorenni: isMinorenne.withMinorenni })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <PmiTitleDivider>Famiglia di provenienza</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <FormChangeParenthood
                  values={formContext.values.otherFamiliars}
                  onChange={onChangeOtherFamiliars}
                  isAire={formContext.values.newFamiliars.some(
                    (el) => el.elementFamilyPractice?.flagAire === '1'
                  )}
                  isOtherFamiliar
                />
              </Grid>
              {formContext.values.otherFamiliars?.every((el) => el.isMinorenne) ? (
                <Grid item xs={12}>
                  <PmiTextField
                    aria-label="Motivo cambio parentela minore"
                    label="Motivo cambio parentela minore *"
                    id="reason"
                    name="reason"
                    fullWidth
                    rows={5}
                    multiline
                    value={formContext.values?.reason || ''}
                    onChange={(ev) => formContext.setFieldValue('reason', ev.target.value)}
                    error={formContext.touched?.reason && Boolean(formContext.errors?.reason)}
                    helperText={formContext.errors?.reason}
                  />
                </Grid>
              ) : undefined}
            </>
          ) : undefined}
          {formContext.values.newFamiliars?.length > 0 && fromPerson !== 'N' ? (
            <>
              <Grid item xs={12}>
                <PmiTitleDivider>Nuova Famiglia</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <FormChangeParenthood
                  values={formContext.values.newFamiliars}
                  onChange={onChangeNewFamiliars}
                />
              </Grid>
            </>
          ) : undefined}
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton
          variant="outlined"
          color="cdmBrand"
          onClick={() => {
            onClose(false);
          }}
        >
          Annulla
        </PmiButton>
        <PmiTooltipError
          show={!!formContext.errors.otherFamiliars || !!formContext.errors.newFamiliars}
          message={
            (formContext.errors.otherFamiliars as string) ||
            (formContext.errors.newFamiliars as string) ||
            ''
          }
        >
          <PmiButton
            variant="contained"
            color="primary"
            disabled={disabledOnConfirm}
            onClick={() => formContext.handleSubmit()}
          >
            Conferma
          </PmiButton>
        </PmiTooltipError>
      </DialogActions>
    </PmiDialog>
  );
};

ModalChangeParenthoodComponent.displayName = 'Modal Change Parenthood component';

const ModalChangeParenthood = memo(ModalChangeParenthoodComponent);

export default ModalChangeParenthood;
